import { FormsyInput } from '@thanx/uikit/input'
import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { Switch } from '@thanx/uikit/switch'
import TooltipLabel from 'components/TooltipLabel'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React from 'react'
import { I18n } from 'react-redux-i18n'
import {
  isFreeVariantKey,
  isModifierDiscountScope,
} from 'scenes/RedeemManager/helpers'
import { DiscountScope, FormModel, useFormModel } from '../FormModel'
import {
  clearModifierDiscounts,
  seedModifierDiscounts,
} from '../FormModel/utils'
import Card from './Card'
import NextButton from './NextButton'

type Props = {
  isDisabled: boolean
  isOpen: boolean
  isCurrent: boolean
  isValidForm: boolean
  onNext: () => void
  onFormChange: (newFormModel: FormModel, isChanged: boolean) => void
  discountConfigIsOpen: boolean
  discountConfigIsCurrent: boolean
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.fixed_price_card'
)
const itemValueT = buildTranslate(
  'redeem_manager.builder.steps.configuration.item_value_card'
)

const validations = {
  greaterThan: 0,
  isRequired: true,
  isNumeric: true,
}

const validationErrors = {
  greaterThan: I18n.t('validations.greater_than', { value: 0 }),
  isRequired: I18n.t('validations.is_required'),
  isNumeric: I18n.t('validations.is_numeric'),
}

const FixedPriceCard: React.FC<Props> = ({
  isDisabled,
  isOpen,
  isCurrent,
  isValidForm,
  onNext,
  onFormChange,
  discountConfigIsOpen,
  discountConfigIsCurrent,
}) => {
  const { model, setModel } = useFormModel()
  const merchant = useCurrentMerchant()
  const { priceCap, discountScope, discount, perceivedValue } = model
  const isFreeVariant = isFreeVariantKey(model.typeItem?.key)
  const isModifierVariant = discountScope === 'required_modifiers'
  // Owner: loyalty. Remove once modifier discounts are GA on every POS and
  // ordering path (Toast in-store, Square V2, Thanx ordering). The same flag
  // gates the admin editor. Here it only hides the radio option; the Modifier
  // discounts fields follow the scope, so existing templates keep them.
  const modifierDiscountsEnabled = useFlag('redeem-modifier-discounts', false)
  // A template already using the scope keeps the option so its saved scope
  // stays visible and selected even when the flag is off.
  const showModifierDiscountOption =
    modifierDiscountsEnabled || isModifierDiscountScope(discountScope)
  // Fixed price doesn't offer modifier discounts yet; a template already on
  // the scope keeps the option so it stays selected.
  const showFixedPriceModifierDiscountOption =
    discountScope === 'base_item_plus_discounted_modifiers'
  // "Selected modifiers" is no longer offered for new choices; a template
  // already on that scope keeps the option so it stays selected.
  const showSelectedModifiersOption =
    discountScope === 'free_base_item_plus_required_modifiers'

  const businessCostTip = itemValueT('cost_to_business_info_v2')

  const handleDiscountScopeChange = (value: string) => {
    if (isDisabled) return
    const scope = value as DiscountScope
    // Entering a modifier-discount scope prefills from the modifier
    // restrictions; leaving one clears the now-hidden modifier discounts.
    const scopedModel = isModifierDiscountScope(scope)
      ? seedModifierDiscounts(model, {
          seedProviders: Boolean(merchant?.has_multi_pos),
        })
      : clearModifierDiscounts(model)
    const updatedModel = { ...scopedModel, discountScope: scope }
    setModel(updatedModel)
    onFormChange(updatedModel, true)
  }

  if (isFreeVariant) {
    return (
      <>
        <Card title={itemValueT('title')} isOpen={isOpen}>
          <p className="grey-70 mt-n3 mb-m">{itemValueT('subtitle')}</p>
          <div className="d-flex">
            <FormsyInput
              // @ts-ignore
              className="width-50 mr-m"
              name="discount"
              disabled={isDisabled}
              label={
                <TooltipLabel
                  label={itemValueT('cost_to_business_label')}
                  tooltip={businessCostTip}
                />
              }
              startEnhancer={itemValueT('dollar')}
              value={discount}
              validations={validations}
              validationErrors={validationErrors}
            />
            <FormsyInput
              // @ts-ignore
              className="width-50"
              name="perceivedValue"
              disabled={isDisabled}
              label={
                <TooltipLabel
                  label={itemValueT('retail_price_label')}
                  tooltip={itemValueT('retail_price_info')}
                />
              }
              startEnhancer={itemValueT('dollar')}
              value={perceivedValue}
              validations={validations}
              validationErrors={validationErrors}
            />
          </div>
          <Switch condition={isCurrent}>
            <NextButton disabled={!isValidForm} onClick={onNext} />
          </Switch>
        </Card>
        <Card title={t('discount_config_title')} isOpen={discountConfigIsOpen}>
          <p className="body-text-4 grey-70 mt-n3 mb-m">
            {t('discount_config_subtitle')}
          </p>
          <RadioGroup
            name="discountScope"
            value={discountScope}
            onChange={value => handleDiscountScopeChange(value)}
            disabled={isDisabled}
          >
            <Radio value="free_base_item">
              <span className="font-weight-bold">
                {t('discount_config_base_item_title')}
              </span>
              <p className="body-text-4 grey-70 mt-xxs mb-0">
                {t('discount_config_base_item_description')}
              </p>
            </Radio>
            {showSelectedModifiersOption && (
              <Radio value="free_base_item_plus_required_modifiers">
                <span className="font-weight-bold">
                  {t('discount_config_required_modifiers_title')}
                </span>
                <p className="body-text-4 grey-70 mt-xxs mb-0">
                  {t('discount_config_required_modifiers_description')}
                </p>
              </Radio>
            )}
            {showModifierDiscountOption && (
              <Radio value="free_base_item_plus_discounted_modifiers">
                <span className="font-weight-bold">
                  {t('discount_config_discounted_modifiers_title')}
                </span>
                <p className="body-text-4 grey-70 mt-xxs mb-0">
                  {t('discount_config_discounted_modifiers_description')}
                </p>
              </Radio>
            )}
            <Radio value="free_base_item_plus_all_modifiers">
              <span className="font-weight-bold">
                {t('discount_config_all_modifiers_title')}
              </span>
              <p className="body-text-4 grey-70 mt-xxs mb-0">
                {t('discount_config_all_modifiers_description')}
              </p>
            </Radio>
          </RadioGroup>
          <Switch condition={discountConfigIsCurrent}>
            <NextButton disabled={!isValidForm} onClick={onNext} />
          </Switch>
        </Card>
      </>
    )
  }

  return (
    <Card title={t('title')} isOpen={isOpen}>
      <FormsyInput
        // @ts-ignore
        name="priceCap"
        disabled={isDisabled}
        label={
          <TooltipLabel
            className={isDisabled ? 'opacity-50' : ''}
            label={t('price_cap_label')}
            sections={[
              { content: t('price_cap_info') },
              { content: t('price_cap_info_eg_1') },
              { content: t('price_cap_info_eg_2') },
              { content: t('price_cap_info_eg_3') },
            ]}
          />
        }
        startEnhancer={t('dollar')}
        value={priceCap}
        validations={validations}
        validationErrors={validationErrors}
      />
      <p className="mt-xs mb-l body-text-4 grey-70">{t('help_text')}</p>

      {isModifierVariant ? (
        <>
          <p className="mt-l mb-s font-weight-bold">
            {t('discount_scope_required_modifiers_title')}
          </p>
          <p className="body-text-4 grey-70 mt-xxs mb-0">
            {t('discount_scope_required_modifiers_description')}
          </p>
        </>
      ) : (
        <>
          <p className="mt-l mb-s font-weight-bold">
            {t('discount_scope_label')}
          </p>
          <RadioGroup
            name="discountScope"
            value={discountScope}
            onChange={value => handleDiscountScopeChange(value)}
            disabled={isDisabled}
          >
            <Radio value="base_item">
              <span className="font-weight-bold">
                {t('discount_scope_base_item')}
              </span>
              <p className="body-text-4 grey-70 mt-xxs mb-0">
                {t('discount_scope_base_item_help')}
              </p>
            </Radio>
            {showFixedPriceModifierDiscountOption && (
              <Radio value="base_item_plus_discounted_modifiers">
                <span className="font-weight-bold">
                  {t('discount_scope_base_item_plus_discounted_modifiers')}
                </span>
                <p className="body-text-4 grey-70 mt-xxs mb-0">
                  {t('discount_scope_base_item_plus_discounted_modifiers_help')}
                </p>
              </Radio>
            )}
            <Radio value="base_item_plus_all_modifiers">
              <span className="font-weight-bold">
                {t('discount_scope_base_item_plus_all_modifiers')}
              </span>
              <p className="body-text-4 grey-70 mt-xxs mb-0">
                {t('discount_scope_base_item_plus_all_modifiers_help')}
              </p>
            </Radio>
            <Radio value="base_item_plus_required_modifiers">
              <span className="font-weight-bold">
                {t('discount_scope_base_item_plus_required_modifiers')}
              </span>
              <p className="body-text-4 grey-70 mt-xxs mb-0">
                {t('discount_scope_base_item_plus_required_modifiers_help')}
              </p>
            </Radio>
          </RadioGroup>
        </>
      )}

      <Switch condition={isCurrent}>
        <NextButton disabled={!isValidForm} onClick={onNext} />
      </Switch>
    </Card>
  )
}

export default FixedPriceCard
